import React, { Component, Fragment } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { withTheme } from '@emotion/react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { ThemeContext } from '../theme';
import { DIALOG_MASK_ZINDEX, DIALOG_ZINDEX } from '../utils';

const propTypes = {
  top: PropTypes.number,
  left: PropTypes.number,
  initHeight: PropTypes.number,
  initWidth: PropTypes.number,
  showHeader: PropTypes.bool,
  showCloseBtn: PropTypes.bool,
  maskBackground: PropTypes.string,
  maskOpacity: PropTypes.number,
  closeOnEscKey: PropTypes.bool,
  closeOnMaskClick: PropTypes.bool,
  isSvgIcon: PropTypes.bool,
  showLoader: PropTypes.bool,
  zIndex: PropTypes.string,
  topBoundaryOffset: Number,
  showMask: PropTypes.bool,
  header: PropTypes.shape({
    overrideTheme: PropTypes.bool,
    text: PropTypes.string,
    backColor: PropTypes.string,
    foreColor: PropTypes.string,
    closeColor: PropTypes.string,
    fontSize: PropTypes.string,
    fontStyle: PropTypes.oneOf(['regular', 'bold', 'italic']),
    icon: PropTypes.string
  }),
  children: PropTypes.element,
  onClose: PropTypes.func
};

const defaultProps = {
  initHeight: 400,
  initWidth: 800,
  showHeader: true,
  showCloseBtn: true,
  maskBackground: '#004080',
  maskOpacity: 0.8,
  closeOnEscKey: false,
  closeOnMaskClick: true,
  isSvgIcon: false,
  showLoader: false,
  topBoundaryOffset: 0,
  showMask: true,
  header: {
    overrideTheme: false,
    text: '',
    backColor: '#1C2A38',
    foreColor: '#507090',
    closeColor: '#FF6060',
    fontStyle: 'bold',
    fontSize: '20px'
  }
};

class DialogBoxComponent extends Component {
  state = {
    isDragging: false,
    top: this.props.top
      ? this.props.top
      : (window.innerHeight / 2) - (this.props.initHeight / 2) - 50,
    left: this.props.left
      ? this.props.left
      : (window.innerWidth / 2) - (this.props.initWidth / 2) - 21,
    width: this.props.initWidth ? this.props.initWidth : 800,
    coordinates: null
  };

  componentDidMount() {
    if (this.props.closeOnEscKey) {
      document.addEventListener('keyup', this.handleKeyUp);
    }
  }

  componentDidUpdate (prevProps) {
    if (prevProps.top !== this.props.top) {
      this.setState((prvState) => (
        {
          ...prvState,
          top: this.props.top
        }
      ));
    }
    if (prevProps.left !== this.props.left) {
      this.setState((prvState) => (
        {
          ...prvState,
          left: this.props.left
        }
      ));
    }
  }

  componentWillUnmount() {
    if (this.props.closeOnEscKey) {
      document.removeEventListener('keyup', this.handleKeyUp);
    }
  }

  // Theme context
  static contextType = ThemeContext;

  handleKeyUp = (e) => {
    e.preventDefault();
    if (e.keyCode === 27) {
      this.props.onClose();
    }
  }

  handleDragHandleMouseDown = (e) => {
    if (e.button !== 0) {
      return; // only left mouse button can trigger draggin
    }

    document.addEventListener('mousemove', this.handleMouseMove);
    // eslint-disable-next-line react/no-find-dom-node
    const modal = ReactDOM.findDOMNode(this.modal);
    this.setState({
      isDragging: true,
      coordinates: {
        x: e.pageX - modal.offsetLeft,
        y: e.pageY - modal.offsetTop
      }
    });

    e.stopPropagation();
    e.preventDefault();
  }

  // Qtx Dialog title bar mouse up event. Stops dragging and remove document mouse move event.
  handleDragHandleMouseUp = (e) => {
    document.removeEventListener('mousemove', this.handleMouseMove);

    this.setState({
      isDragging: false
    });

    e.stopPropagation();
    e.preventDefault();
  }

  handleMouseMove = (e) => {
    if (this.state.isDragging) {
      let currLeft = e.pageX - this.state.coordinates.x;
      let currTop = e.pageY - this.state.coordinates.y;

      // Left bound
      if (e.pageX - this.state.coordinates.x < 2) {
        currLeft = 2;
      }

      // Right bound
      if (e.pageX - this.state.coordinates.x > (window.innerWidth - this.props.initWidth)) {
        // currLeft = window.initWidth - this.props.initWidth;
        currLeft = window.innerWidth - this.props.initWidth;
      }

      // Upper bound
      if (e.pageY - this.state.coordinates.y < 2) {
        // currTop = 0;
        currTop = this.props.topBoundaryOffset;
      }
      // Lower bound
      if (e.pageY - this.state.coordinates.y > window.innerHeight - this.props.initHeight) {
        const modalHeight = this.modal.getBoundingClientRect().height;
        // currTop = window.innerHeight - this.props.initHeight + this.props.topBoundaryOffset;
        currTop = window.innerHeight - modalHeight + this.props.topBoundaryOffset;
      }

      this.setState({
        left: currLeft,
        top: currTop
      });
    }

    e.stopPropagation();
    e.preventDefault();
  }

  render() {
    const {
      width,
      top,
      left,
      zIndex
    } = this.state;

    const dialogBoxTheme = this.props.theme.dialogBox ? this.props.theme.dialogBox : this.context.theme.dialogBox;
    const {
      borderColor,
      headerBackground,
      headerColor,
      headerFontWeight,
      headerFontSize,
      bodyBackground,
      headerHeight,
      dialogBoxRadius,
      headerBorderColor,
      headerTextPadding,
      maskOpacity,
      maskBackground,
      bodyColor,
      closeBtnHoverBg
    } = dialogBoxTheme;

    const styles = StyleSheet.create({
      mask: {
        position: 'fixed',
        height: '100vh',
        width: '100vw',
        left: '0',
        right: '0',
        bottom: '0',
        zIndex: this.props.maskZIndex || DIALOG_MASK_ZINDEX,
        background: this.props.maskBackground,
        opacity: this.props.maskOpacity
      },
      dialogBox: {
        position: 'fixed',
        zIndex: (this.props.zIndex ? `${this.props.zIndex}` : DIALOG_ZINDEX),
        // zIndex: this.props.zIndex || DIALOG_ZINDEX,
        boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.5)',
        width,
        top,
        left,
        fontSize: window.innerWidth <= 576 ? '0.75rem' : '1rem',
        border: `1px solid ${borderColor}`,
        borderRadius: dialogBoxRadius,
        background: bodyBackground
      },
      dialogBoxContainer: {
        marginTop: '40px',
        padding: '0'
      },
      dialogBoxHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'absolute',
        right: '0',
        top: '0',
        marginBottom: '10px',
        cursor: 'move',
        width: `${width - 2}px`,
        background: this.props.showHeader ? headerBackground : 'transparent',
        color: !this.props.header.overrideTheme ? headerColor : this.props.header.foreColor,
        fontWeight: headerFontWeight,
        fontSize: !this.props.header.overrideTheme ? headerFontSize : this.props.header.fontSize,
        minHeight: headerHeight,
        maxHeight: headerHeight,
        borderBottom: this.props.showHeader ? `1px solid ${headerBorderColor}` : 'none',
        padding: headerTextPadding
      },
      dialogHeaderIcon: {
        width: '44px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      icon: {
        maxHeight: '24px',
        fontSize: '24px'
      },
      headerText: {
        width: `${this.state.width - 48}px`,
        maxWidth: `${this.state.width - 48}px`
      },
      headerCloseBtn: {
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      closeBtnIcon: {
        cursor: 'pointer',
        ':hover': {
          color: closeBtnHoverBg
        }
      }
    });

    return (
      <Fragment>
        {/* DIALOG BOX MASK */}
        <div
          className={css(styles.mask)}
          style={{ display: this.props.showMask ? 'block' : 'none' }}
          onClick={() => {
            if (this.props.closeOnMaskClick) {
              this.props.onClose();
            }
          }}
        />

        {/* DAILOG BOX */}
        <div
          ref={(node) => {
            this.modal = node;
          }}
          className={css(styles.dialogBox)}
        >
          {/* Dialog box container */}
          <div className={css(styles.dialogBoxContainer)}>
            {this.props.children}
          </div>

          { /* MODAL HEADER */}
          <div
            className={css(styles.dialogBoxHeader)}
            onMouseDown={this.handleDragHandleMouseDown}
            onMouseUp={this.handleDragHandleMouseUp}
          >
            {/* Header icon */}
            {this.props.header.icon
              ? <div className={css(styles.dialogHeaderIcon)}>
                {!this.props.isSvgIcon
                  ? <i className={`${this.props.header.icon} ${css(styles.icon)}`} />
                  : <img src={this.props.header.icon} alt="Header icon" />
                }
              </div>
              : null
            }
            {/* Header text */}
            <div className={`text-truncate ${css(styles.headerText)}`}>
              {this.props.header.text}
            </div>
            {/* Header close button */}
            {this.props.showCloseBtn &&
              <div className={css(styles.headerCloseBtn)}>
                <i
                  className={`fa fa-times-circle mr-0 ${css(styles.closeBtnIcon)}`}
                  onClick={this.props.onClose}
                />
              </div>
            }
          </div>
        </div>
      </Fragment>
    );
  }
}

DialogBoxComponent.propTypes = propTypes;
DialogBoxComponent.defaultProps = defaultProps;

export const DialogBox = withTheme(DialogBoxComponent);
